"use client";
import React from "react";
import { motion } from "framer-motion";

export function CurvedCard({ children, auroraComponent, topOffset, customHeight, fullRounded, honeycombOverlay, blurAmount, contentJustify = 'center', contentPaddingTop = '5rem' }: { children: React.ReactNode, auroraComponent?: React.ReactNode, topOffset?: string, customHeight?: string, fullRounded?: boolean, honeycombOverlay?: boolean, blurAmount?: number, contentJustify?: 'start' | 'center' | 'end', contentPaddingTop?: string }) {
  return (
    <div className="relative flex flex-col items-center justify-start w-full h-auto">
      {/* Curved card background */}
      <div className={`absolute left-0 w-full overflow-hidden z-0`} style={{ top: topOffset || '-4rem', height: customHeight || '135vh' }}>
        <div className="relative w-full h-full">
          {/* Aurora effect inside the card */}
          {auroraComponent && (
            <div className={`absolute inset-0 overflow-hidden ${fullRounded ? 'rounded-[24px]' : 'rounded-t-[24px]'}`}>
              {auroraComponent}
            </div>
          )}

          {/* Main curved card with blue gradient overlay */}
          <div
            className={`absolute inset-0 h-full ${fullRounded ? 'rounded-[24px]' : 'rounded-t-[24px]'}`}
            style={{
              background:
                "linear-gradient(135deg, rgba(16, 185, 212, 0.02) 0%, rgba(16, 185, 212, 0.01) 50%, rgba(16, 185, 212, 0.005) 100%)",
              backdropFilter: `blur(${typeof blurAmount === 'number' ? blurAmount : 3}px)`,
              boxShadow: "0 8px 32px rgba(16, 185, 212, 0.05)"
            }}
          />

          {/* Optional subtle honeycomb overlay (tiny pattern) - placed above gradient */}
          {honeycombOverlay && (
            <div
              className={`absolute inset-0 pointer-events-none ${fullRounded ? 'rounded-[24px]' : 'rounded-t-[24px]'}`}
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='8' height='9' viewBox='0 0 8 9'><path d='M2 1.5 L6 1.5 L8 4.5 L6 7.5 L2 7.5 L0 4.5 Z' fill='none' stroke='%23ffffff' stroke-opacity='0.20' stroke-width='0.5'/></svg>\")",
                backgroundSize: '8px 9px',
                backgroundPosition: 'center',
                opacity: 0.28,
                mixBlendMode: 'overlay'
              }}
            />
          )}

          {/* Top accent line */}
          <motion.div
            initial={{ width: "15rem", opacity: 0.5 }}
            whileInView={{ width: "40rem", opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8, ease: "easeInOut" }}
            className="absolute inset-auto z-10 h-0.5 w-[40rem] top-0 left-1/2 -translate-x-1/2 bg-cyan-400 rounded-full"
          />

          {/* Subtle inner glow */}
          <motion.div
            initial={{ opacity: 0.3, scale: 0.8 }}
            whileInView={{ opacity: 0.6, scale: 1 }}
            transition={{ delay: 0.5, duration: 1, ease: "easeOut" }}
            className="absolute top-4 left-1/2 -translate-x-1/2 w-[60%] h-32 bg-gradient-radial from-cyan-400/20 via-cyan-400/5 to-transparent rounded-full blur-xl"
          />
        </div>
      </div>

      {/* Content passed as children, rendered on top */}
      <div className={`relative z-10 flex flex-col items-center w-full ${contentJustify === 'start' ? 'justify-start' : contentJustify === 'end' ? 'justify-end' : 'justify-center'}`} style={{ paddingTop: contentPaddingTop }}>
        {children}
      </div>
    </div>
  );
}
